// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: who reacted, on a phone (JP 2026-10-29, design/reaction-info concept A, compact). Holding a
// reaction lifts the message's reactions out of a blurred chat and opens a small glass panel from
// them: a tab per emoji (the held one chosen) plus All, then the people. Your own row takes your
// reaction back. Same material as the GlassMenu long-press menus.

import {useBottomSheetBackHandler} from '@app/features/app/hooks/useBottomSheetBackHandler';
import Authentication from '@app/features/auth/state/Authentication';
import styles from '@app/features/channel/components/ReactionInfoPanel.module.css';
import Channels from '@app/features/channel/state/Channels';
import * as ReactionCommands from '@app/features/messaging/commands/ReactionCommands';
import {useMessageReactions} from '@app/features/messaging/hooks/useMessageReactionStore';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import MessageReactions from '@app/features/messaging/state/MessageReactions';
import {
	getEmojiName,
	getNativeEmoji,
	getReactionKey,
	type ReactionEmoji,
	useEmojiURL,
} from '@app/features/messaging/utils/ReactionUtils';
import {Avatar} from '@app/features/ui/components/Avatar';
import glass from '@app/features/ui/glass_menu/GlassMenu.module.css';
import {resolvePortalHost, usePortalHost} from '@app/features/ui/overlay/PortalHostContext';
import LayerManager from '@app/features/ui/state/LayerManager';
import OverlayStack from '@app/features/ui/state/OverlayStack';
import type {User} from '@app/features/user/models/User';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import type {MessageReaction} from '@konvo/schema/src/domains/message/MessageResponseSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {useCallback, useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore} from 'react';
import {createPortal} from 'react-dom';

const WHO_REACTED_DESCRIPTOR = msg({
	message: 'Who reacted',
	comment: 'Phone panel listing who reacted to a message: its accessible name.',
});
const ALL_DESCRIPTOR = msg({
	message: 'All',
	comment: 'Phone panel listing who reacted to a message: the tab that shows every reaction. Keep it short.',
});
const YOU_DESCRIPTOR = msg({
	message: 'You',
	comment: 'Phone panel listing who reacted to a message: your own row instead of your name.',
});
const TAP_TO_REMOVE_DESCRIPTOR = msg({
	message: 'Tap to remove',
	comment: 'Phone panel listing who reacted to a message: under your own row; tapping it takes your reaction back.',
});
const SHOW_MORE_DESCRIPTOR = msg({
	message: 'Show more',
	comment: 'Phone panel listing who reacted to a message: loads more people when there are many.',
});
const LOADING_DESCRIPTOR = msg({
	message: 'Loading…',
	comment: 'Phone panel listing who reacted to a message: while the people are fetched.',
});

const EDGE = 12;
const GAP = 14;
const WIDTH = 320;
/** All, or one emoji's reaction key. */
type Tab = 'all' | string;

interface Row {
	user: User;
	reaction: MessageReaction;
}

/** The people behind every reaction on the message, kept current as they load or change. */
function useReactors(messageId: string, reactions: ReadonlyArray<MessageReaction>) {
	const subscribe = useCallback(
		(listener: () => void) => {
			const offs = reactions.map((reaction) => MessageReactions.subscribeReaction(messageId, reaction.emoji, listener));
			return () => {
				for (const off of offs) off();
			};
		},
		[messageId, reactions],
	);
	const getSnapshot = useCallback(
		() => reactions.map((reaction) => MessageReactions.getReactionVersion(messageId, reaction.emoji)).join(','),
		[messageId, reactions],
	);
	useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
	return reactions.map((reaction) => ({
		reaction,
		users: MessageReactions.getReactions(messageId, reaction.emoji),
		status: MessageReactions.getFetchStatus(messageId, reaction.emoji),
		hasMore: MessageReactions.getHasMore(messageId, reaction.emoji),
		fetchedLimit: MessageReactions.getInitialFetchLimit(messageId, reaction.emoji),
	}));
}

function EmojiGlyph({emoji, className}: {emoji: ReactionEmoji; className?: string}) {
	const url = useEmojiURL({emoji});
	const native = getNativeEmoji(emoji);
	if (url) return <img src={url} alt={getEmojiName(emoji)} className={clsx(styles.emojiImage, className)} />;
	return (
		<span className={clsx(styles.emoji, className)} role="img" aria-label={getEmojiName(emoji)}>
			{native ?? getEmojiName(emoji)}
		</span>
	);
}

interface Placement {
	top: number;
	left: number;
	width: number;
	maxHeight: number;
	origin: string;
}

/** Below the held reactions, left edges lined up; above them when there is no room below. */
function place(rect: DOMRect, height: number): Placement {
	const vw = window.innerWidth;
	const vh = window.visualViewport?.height ?? window.innerHeight;
	const width = Math.min(WIDTH, vw - EDGE * 2);
	const left = Math.min(Math.max(rect.left - 6, EDGE), vw - width - EDGE);
	const below = rect.bottom + GAP;
	const above = rect.top - GAP;
	const x = `${Math.max(0, rect.left - left + 24)}px`;
	if (below + height <= vh - EDGE || vh - below >= above)
		return {top: below, left, width, maxHeight: vh - EDGE - below, origin: `${x} 0`};
	const maxHeight = above - EDGE;
	return {top: above - Math.min(height, maxHeight), left, width, maxHeight, origin: `${x} 100%`};
}

export interface ReactionInfoPanelProps {
	isOpen: boolean;
	onClose: () => void;
	message: Message;
	/** The message's reactions row: lifted above the blur, and where the panel opens from. */
	anchor: HTMLElement | null;
	/** The reaction held: its tab is chosen. */
	openTo: ReactionEmoji | null;
}

export function ReactionInfoPanel({isOpen, onClose, message, anchor, openTo}: ReactionInfoPanelProps) {
	const {i18n} = useLingui();
	const portalHost = usePortalHost();
	const reactions = useMessageReactions(message.id);
	const reactors = useReactors(message.id, reactions);
	const [tab, setTab] = useState<Tab>('all');
	const [zIndex, setZIndex] = useState<number | null>(null);
	const [placement, setPlacement] = useState<Placement | null>(null);
	const [lifted, setLifted] = useState<DOMRect | null>(null);
	const panelRef = useRef<HTMLDivElement | null>(null);
	const liftRef = useRef<HTMLDivElement | null>(null);
	// The click a phone sends when the held finger lifts lands on the dim: only a press that
	// starts on the dim closes the panel.
	const dimPressed = useRef(false);
	const channel = Channels.getChannel(message.channelId);
	const guildId = channel?.guildId ?? undefined;
	const me = Authentication.currentUserId;

	useEffect(() => {
		if (!isOpen) return;
		setTab(openTo ? getReactionKey(message.id, openTo) : 'all');
		setZIndex(OverlayStack.acquire());
		return () => {
			OverlayStack.release();
			setZIndex(null);
			setPlacement(null);
			setLifted(null);
		};
	}, [isOpen, message.id, openTo]);

	// Fetch everyone behind each reaction once (the desktop tooltip may have fetched only a few).
	useEffect(() => {
		if (!isOpen) return;
		for (const {reaction, users, status, fetchedLimit} of reactors) {
			const wanted = Math.min(100, reaction.count);
			if (status === 'pending' || wanted === 0) continue;
			if (status === 'success' && (fetchedLimit >= wanted || users.length >= wanted)) continue;
			if (status === 'error') continue;
			ReactionCommands.getReactions(message.channelId, message.id, reaction.emoji, {
				limit: 100,
				totalCount: reaction.count,
			}).catch(() => {});
		}
	}, [isOpen, message.channelId, message.id, reactors]);

	// A tab whose reaction went away falls back to All; the panel closes when none are left.
	useEffect(() => {
		if (!isOpen) return;
		if (reactions.length === 0) onClose();
		else if (tab !== 'all' && !reactions.some((reaction) => getReactionKey(message.id, reaction.emoji) === tab))
			setTab('all');
	}, [isOpen, message.id, onClose, reactions, tab]);

	const shownReactors =
		tab === 'all' ? reactors : reactors.filter((r) => getReactionKey(message.id, r.reaction.emoji) === tab);
	const everyone: Array<Row> = shownReactors.flatMap(({reaction, users}) => users.map((user) => ({user, reaction})));
	// Yours first, as on WhatsApp: it is the one you can act on.
	const rows = [...everyone.filter((row) => row.user.id === me), ...everyone.filter((row) => row.user.id !== me)];
	const loading = rows.length === 0 && shownReactors.some((r) => r.status === 'pending' || r.status === 'idle');
	const more = shownReactors.filter((r) => r.hasMore);
	const total = reactions.reduce((sum, reaction) => sum + reaction.count, 0);

	useLayoutEffect(() => {
		if (!isOpen || !panelRef.current || !anchor?.isConnected) return;
		const rect = anchor.getBoundingClientRect();
		setPlacement(place(rect, panelRef.current.scrollHeight));
		setLifted(rect);
	}, [anchor, isOpen, zIndex, rows.length, loading]);

	// The lifted copy of the reactions: their own nodes, so it looks exactly like them.
	useLayoutEffect(() => {
		if (lifted && liftRef.current && anchor) liftRef.current.replaceChildren(anchor.cloneNode(true));
	}, [anchor, lifted, reactions]);

	const layerKey = useId();
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;
	useEffect(() => {
		if (!isOpen) return;
		LayerManager.addLayer('contextmenu', layerKey, () => onCloseRef.current());
		return () => LayerManager.removeLayer('contextmenu', layerKey);
	}, [isOpen, layerKey]);
	useBottomSheetBackHandler(isOpen, onClose);

	if (!isOpen || zIndex === null || typeof document === 'undefined') return null;
	const tabs: Array<{key: Tab; label: React.ReactNode; count: number}> = [
		{key: 'all', label: i18n._(ALL_DESCRIPTOR), count: total},
		...reactions.map((reaction) => ({
			key: getReactionKey(message.id, reaction.emoji),
			label: <EmojiGlyph emoji={reaction.emoji} className={styles.tabEmoji} />,
			count: reaction.count,
		})),
	];
	return createPortal(
		<div className={clsx(glass.root, glass.auto)} style={{zIndex}} data-flx="channel.reaction-info">
			<div
				className={clsx(glass.dim, glass.dimLift)}
				onPointerDown={() => {
					dimPressed.current = true;
				}}
				onClick={() => {
					if (dimPressed.current) onClose();
					dimPressed.current = false;
				}}
				aria-hidden="true"
			/>
			{lifted && (
				<div
					ref={liftRef}
					className={clsx(glass.lifted, styles.lifted)}
					style={{top: lifted.top - 4, left: lifted.left - 6, width: lifted.width + 12}}
					aria-hidden="true"
				/>
			)}
			<div
				ref={panelRef}
				role="dialog"
				aria-label={i18n._(WHO_REACTED_DESCRIPTOR)}
				className={clsx(styles.panel, placement && styles.panelShown)}
				style={
					placement
						? {
								top: placement.top,
								left: placement.left,
								width: placement.width,
								maxHeight: placement.maxHeight,
								transformOrigin: placement.origin,
							}
						: undefined
				}
				data-flx="channel.reaction-info.panel"
			>
				{reactions.length > 1 ? (
					<div className={styles.tabs} role="tablist">
						{tabs.map((item) => (
							<button
								key={item.key}
								type="button"
								role="tab"
								aria-selected={tab === item.key}
								className={clsx(styles.tab, tab === item.key && styles.tabOn)}
								onClick={() => setTab(item.key)}
								data-flx="channel.reaction-info.tab"
								data-tab={item.key === 'all' ? 'all' : undefined}
							>
								{item.label}
								<span className={styles.tabCount}>{item.count}</span>
							</button>
						))}
					</div>
				) : null}
				<div className={styles.list}>
					{loading && <div className={styles.note}>{i18n._(LOADING_DESCRIPTOR)}</div>}
					{rows.map(({user, reaction}) => {
						const own = user.id === me;
						const name = own ? i18n._(YOU_DESCRIPTOR) : NicknameUtils.getNickname(user, guildId, message.channelId);
						const body = (
							<>
								<Avatar user={user} size={28} guildId={guildId} className={styles.avatar} />
								<span className={styles.who}>
									<span className={styles.name}>{name}</span>
									{own && <span className={styles.sub}>{i18n._(TAP_TO_REMOVE_DESCRIPTOR)}</span>}
								</span>
								<EmojiGlyph emoji={reaction.emoji} />
							</>
						);
						const key = `${user.id}:${getReactionKey(message.id, reaction.emoji)}`;
						return own ? (
							<button
								key={key}
								type="button"
								className={clsx(styles.row, styles.rowButton)}
								onClick={() => ReactionCommands.removeReaction(i18n, message.channelId, message.id, reaction.emoji)}
								data-flx="channel.reaction-info.own"
							>
								{body}
							</button>
						) : (
							<div key={key} className={styles.row} data-flx="channel.reaction-info.row" data-user-id={user.id}>
								{body}
							</div>
						);
					})}
					{more.length > 0 && (
						<button
							type="button"
							className={clsx(styles.row, styles.rowButton, styles.more)}
							onClick={() => {
								for (const r of more)
									void ReactionCommands.loadMoreReactions(message.channelId, message.id, r.reaction.emoji, {
										totalCount: r.reaction.count,
									});
							}}
							data-flx="channel.reaction-info.more"
						>
							{i18n._(SHOW_MORE_DESCRIPTOR)}
						</button>
					)}
				</div>
			</div>
		</div>,
		resolvePortalHost(portalHost),
	);
}
